import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Checkbox, Col, Row, Tooltip } from 'antd'
import { MenuOutlined } from '@ant-design/icons'
import clsx from 'clsx'

import B2becQuantity from '@/components/B2becQuantity'
import QUANTITY_TYPE from '@/components/B2becQuantity/constants'
import B2BecLink from '@/components/B2BLink'
import ConfirmModal from '@/components/ConfirmModal'
import FormatPrice from '@/components/FormatPrice'
import { HeartFilledIcon } from '@/components/Icons'
import ImageWithFallBack from '@/components/ImageWithFallback'
import { useAppSelector } from '@/store/hook'
import { selectHideListPriceGlobal } from '@/store/selectors/configSelector'
import { useDeviceDetect } from '@/hooks'
import usePermission from '@/hooks/usePermission'
import {
  MODULE_SHOP_NAVIGATION_RIGHTS,
  MODULE_WISHLIST_RIGHTS,
} from '@/libs/constants/modulerights'

import styles from './WishlistProductCard.module.scss'

const WishlistProductCard = (props) => {
  const {
    name,
    category,
    img = '',
    imageUrl = '',
    materialNumber = '',
    materialId = '',
    listPrice,
    addToCartBtn,
    comparingProductIds = [],
    onCheckHandler,
    isCompareCheckboxDisplayed = false,
    requiresInstruction: isBProgramProduct = false,
    currencyUnit = '',
    displayAddToCart: shouldDisplayAddToCart = true,
    shouldDisplayListPrice = true,
    quantity = 1,
    isRemovingProduct = false,
    onRemoveWishlistProductHandler,
    onChangeProductQuantityHandler,
    isSharedWishlist = false,
    dragHandleProps,
    isDragging = false,
  } = props

  const { t } = useTranslation()
  const { isLargeScreen } = useDeviceDetect()
  const [isShowRemoveModal, setShowRemoveModal] = useState(false)

  const isPriceEqualZero = +listPrice === 0 || listPrice === '' || !listPrice
  const {
    verifiedPermissions: [
      hasAddToCartPermission,
      hasUpdateWishlistQuantityPermission,
      hasCompareProductPermission,
    ],
  } = usePermission([
    MODULE_WISHLIST_RIGHTS.ADD_WISHLIST_TO_CART,
    MODULE_WISHLIST_RIGHTS.ADD_PRODUCT_CHANGE_WISHLIST,
    MODULE_SHOP_NAVIGATION_RIGHTS.PRODUCT_COMPARISION,
  ])
  const isDisableQuantity = isSharedWishlist || !hasUpdateWishlistQuantityPermission
  const shouldHideAddToCartButton = Boolean(
    !shouldDisplayAddToCart
      || isBProgramProduct
      || isPriceEqualZero
      || !hasAddToCartPermission,
  )
  const shouldHideListPriceGlobally = useAppSelector(selectHideListPriceGlobal)

  const isComparingProduct = (comparingProductIds ?? []).includes(
    materialNumber,
  )

  const onDisplayRemoveModal = useCallback(() => {
    if (!isSharedWishlist) {
      setShowRemoveModal(true)
    }
  }, [isSharedWishlist])

  const renderHoverLayer = () => (
    <div className={styles['hover-layer']}>
      <Row className={clsx(styles['functions-wrapper'], !isDragging && 'fadeIn-1')}>
        <Col>
          {hasCompareProductPermission && (
            <Checkbox
              checked={isComparingProduct}
              className="kaercher-checkbox"
              onChange={(e) =>
                onCheckHandler(
                  {
                    materialNumber,
                    materialName: name,
                    categoryName: category,
                    listPrice,
                    currencyUnit,
                    pictureUrl: img || imageUrl,
                    displayListPrice: shouldDisplayListPrice,
                  },
                  e,
                )}
            >
              {t('productComparison.button')}
            </Checkbox>
          )}
        </Col>
        <Col style={{ textAlign: 'right' }}>
          {shouldHideAddToCartButton ? null : addToCartBtn}
        </Col>
      </Row>
    </div>
  )

  const mobileProductCard = () => {
    return (
      <Card className="wishlist-card-mobile">
        <div className={styles['mobile-top-bar']}>
          {dragHandleProps && (
            <div
              className={styles['drag-handle-mobile']}
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuOutlined />
            </div>
          )}
          <div className="add-to-wishlist">
            <button
              className="button-as-link"
              disabled={isRemovingProduct}
              type="button"
              onClick={onDisplayRemoveModal}
            >
              <HeartFilledIcon />
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', height: 'inherit' }}>
          <div className={styles['left-wrapper']}>
            <B2BecLink
              className="fadeIn-2"
              to={`/product/${materialNumber || materialId}`}
            >
              <ImageWithFallBack
                alt={name}
                className="product-card__content-image"
                src={img}
              />
            </B2BecLink>

            <B2becQuantity
              initialQuantity={quantity}
              inputClassName={styles['quantity-input']}
              isParentDisabled={isDisableQuantity}
              materialNumber={materialNumber || materialId}
              type={QUANTITY_TYPE.WISHLIST_DETAIL}
              wrapperClassName={styles.quantity}
              onChange={onChangeProductQuantityHandler}
            />
          </div>

          <div className="product-card__content--right-wrapper">
            <B2BecLink
              className="link-normalize fadeIn-08"
              style={{ marginBottom: 13.3 }}
              to={`/product/${materialNumber || materialId}`}
            >
              <p className="fadeIn-05 fontBold product-card__content-name">
                {name}
              </p>

              <p className="fadeIn-05 product-card__content-material-number">
                {`${t('productCard.itemNumber')} `}
                {materialNumber || materialId}
              </p>
            </B2BecLink>

            <div className="product-card__content--lower-wrapper">
              {shouldHideListPriceGlobally
                ? null
                : (
                  <div className="fadeIn-1 product-card__list-price-wrapper">
                    <span className="product-card__list-price-text">
                      {t('productCard.listPrice')}
                    </span>
                    <span className="fontBold product-card__list-price-number">
                      {isPriceEqualZero
                        ? (
                          t('productCard.onRequest')
                        )
                        : (
                          <FormatPrice
                            currencyUnit={currencyUnit}
                            price={listPrice}
                          />
                        )}
                    </span>
                  </div>
                )}

              {shouldHideAddToCartButton ? null : addToCartBtn}
            </div>
          </div>
        </div>
      </Card>
    )
  }

  const desktopProductCard = () => {
    return (
      <div className={clsx(styles.container, isDragging && styles.isDragging)}>
        <div>
          {dragHandleProps && (
            <div
              className={styles.dragHandle}
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuOutlined />
            </div>
          )}
          <div className={styles['heart-container']}>
            <button
              className={styles.heart}
              disabled={isRemovingProduct}
              type="button"
              onClick={onDisplayRemoveModal}
            >
              <HeartFilledIcon />
            </button>
          </div>
          <div className={styles.imageWrapper}>
            <B2becQuantity
              initialQuantity={quantity}
              inputClassName={styles['quantity-input']}
              isParentDisabled={isDisableQuantity}
              materialNumber={materialNumber || materialId}
              type={QUANTITY_TYPE.WISHLIST_DETAIL}
              wrapperClassName={styles.quantity}
              onChange={onChangeProductQuantityHandler}
            />
            <B2BecLink
              className={clsx('ant-card-cover', !isDragging && 'fadeIn-2')}
              to={`/product/${materialNumber || materialId}`}
            >
              <ImageWithFallBack alt={name} src={img || imageUrl} />
            </B2BecLink>
          </div>
          <B2BecLink
            className={styles['link-normalize']}
            to={`/product/${materialNumber || materialId}`}
          >
            <Tooltip title={name}>
              <p className={styles['product-card-title']}>{name}</p>
            </Tooltip>
          </B2BecLink>
          <B2BecLink
            className={styles['link-normalize']}
            to={`/product/${materialNumber || materialId}`}
          >
            <p className={styles['product-card-item-number']}>
              {`${t('productCard.itemNumber')} `}
              {materialNumber || materialId}
            </p>
          </B2BecLink>
          {shouldHideListPriceGlobally ? null : (
            <Row
              className={clsx(styles['list-price-wrapper'], !isDragging && 'fadeIn-1')}
              justify="space-between"
            >
              <Col className={styles['list-price-title']}>
                {t('productCard.listPrice')}
              </Col>
              <Col className={styles['list-price-price']}>
                {isPriceEqualZero
                    || !shouldDisplayListPrice
                    || (shouldDisplayListPrice && isBProgramProduct)
                  ? (
                    t('productCard.onRequest')
                  )
                  : <FormatPrice currencyUnit={currencyUnit} price={listPrice} />}
              </Col>
            </Row>
          )}
        </div>
        {isLargeScreen && isCompareCheckboxDisplayed && renderHoverLayer()}
      </div>
    )
  }

  return (
    <>
      {isLargeScreen ? desktopProductCard() : mobileProductCard()}

      <ConfirmModal
        isShowModal={isShowRemoveModal && !isSharedWishlist}
        modalContent={t('wishlist.modal.removeProduct.modalContent')}
        modalTitle={t('wishlist.modal.removeProduct.title')}
        setIsShowModal={setShowRemoveModal}
        confirmActionHandler={() => onRemoveWishlistProductHandler(materialNumber)}
      />
    </>
  )
}

export default WishlistProductCard
